import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { TrendingUp, BarChart3, Calendar, AlertTriangle } from 'lucide-react';
import { monthlyAnalysisAPI, formatNumber as formatNumberUtil } from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MonthlyAnalysis = ({ month, year }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriberTrendsData, setSubscriberTrendsData] = useState(null);
  const [revenueTrendsData, setRevenueTrendsData] = useState(null);
  const [currentSummary, setCurrentSummary] = useState(null);
  const [ytdPerformance, setYtdPerformance] = useState(null);
  const monthParam = month != null ? month : new Date().getMonth() + 1;
  const yearParam = year != null ? year : new Date().getFullYear();

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data concurrently
      const periodParams = { month: monthParam, year: yearParam };
      const [
        subscriberTrends,
        revenueTrends,
        summary,
        ytdData
      ] = await Promise.all([
        monthlyAnalysisAPI.getSubscriberTrends(periodParams),
        monthlyAnalysisAPI.getRevenueTrends(periodParams),
        monthlyAnalysisAPI.getCurrentSummary(periodParams),
        monthlyAnalysisAPI.getYtdPerformance(periodParams)
      ]);

      setSubscriberTrendsData(subscriberTrends.data);
      setRevenueTrendsData(revenueTrends.data);
      setCurrentSummary(summary.data);
      setYtdPerformance(ytdData.data);

    } catch (err) {
      console.error('Error fetching monthly analysis data:', err);
      setError('Failed to load monthly analysis data');
    } finally {
      setLoading(false);
    }
  };

  // Generate chart data from API response (use year from API when provided)
  const subscriberTrendLabels = subscriberTrendsData
    ? (subscriberTrendsData.labels && subscriberTrendsData.labels.length > 0)
      ? subscriberTrendsData.labels
      : (subscriberTrendsData.months || []).map((m, i) => {
          const str = String(m);
          if (/\b20\d{2}\b/.test(str)) return str; // already has year
          const y = (subscriberTrendsData.years && subscriberTrendsData.years[i]) ?? subscriberTrendsData.year ?? yearParam;
          return `${str} ${y}`;
        })
    : [];
  const subscriberTrendData = subscriberTrendsData ? {
    labels: subscriberTrendLabels,
    datasets: [
      {
        label: 'Total Subscribers',
        data: subscriberTrendsData.total_subscribers,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6
      },
      {
        label: 'Active Subscribers',
        data: subscriberTrendsData.active_subscribers,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6
      }
    ]
  } : null;

  // Revenue chart data from API response
  const revenueData = revenueTrendsData ? {
    labels: revenueTrendsData.months,
    datasets: [
      {
        label: 'MBB Revenue',
        data: revenueTrendsData.mbb_revenue,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 1
      },
      {
        label: 'FTB Revenue',
        data: revenueTrendsData.ftb_revenue,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: '#10b981',
        borderWidth: 1
      }
    ]
  } : null;

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: true,
        text: 'Subscriber Growth Trend',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Subscribers'
        },
        ticks: {
          callback: function(value) {
            return (value / 1000000).toFixed(1) + 'M';
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: true,
        text: 'Monthly Revenue by Service Type',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${Number(context.parsed.y).toLocaleString()}M ₭`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Revenue (Millions Kip)'
        },
        stacked: false,
        ticks: {
          callback: function(value) {
            return Number(value).toLocaleString() + 'M ₭';
          }
        }
      }
    }
  };

  // Format number function
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return formatNumberUtil(num);
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center mb-6">
          <Calendar className="h-6 w-6 text-primary-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Monthly Analysis Dashboard</h2>
            <p className="text-gray-600">Loading subscriber trends and revenue analysis...</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-8">
        <div className="flex items-center mb-6">
          <Calendar className="h-6 w-6 text-primary-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Monthly Analysis Dashboard</h2>
            <p className="text-gray-600">Subscriber trends and revenue analysis over time</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">Error Loading Data</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button 
                onClick={fetchAllData}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center mb-6">
        <Calendar className="h-6 w-6 text-primary-600 mr-3" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">Monthly Analysis Dashboard</h2>
          <p className="text-gray-600">Subscriber trends and revenue analysis over time</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Subscribers</p>
              <p className="text-2xl font-bold">{formatNumber(currentSummary?.total_subscribers || 0)}</p>
              <p className="text-blue-100 text-xs mt-1">
                {currentSummary?.growth_rate > 0 ? '+' : ''}{currentSummary?.growth_rate?.toFixed(2) || '0.00'}% this month
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Active Rate</p>
              <p className="text-2xl font-bold">{currentSummary?.active_rate?.toFixed(1) || '0.0'}%</p>
              <p className="text-green-100 text-xs mt-1">{formatNumber(currentSummary?.active_subscribers || 0)} active</p>
            </div>
            <BarChart3 className="h-8 w-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold">{currentSummary?.total_revenue?.toFixed(1) || '0.0'}B ₭</p>
              <p className="text-purple-100 text-xs mt-1">Current month</p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Collection Rate</p>
              <p className="text-2xl font-bold">{currentSummary?.collection_rate?.toFixed(1) || '0.0'}%</p>
              <p className="text-orange-100 text-xs mt-1">Fee collection</p>
            </div>
            <BarChart3 className="h-8 w-8 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Line Chart - Subscriber Trend */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
              Subscriber Growth Trend
            </h3>
            <div className="flex space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-gray-600">Total</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-gray-600">Active</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            {subscriberTrendData ? (
              <Line data={subscriberTrendData} options={lineChartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading chart data...</div>
              </div>
            )}
          </div>
        </div>

        {/* Bar Chart - Revenue per Month */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-green-500" />
              Monthly Revenue Analysis
            </h3>
            <div className="flex space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-gray-600">MBB</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-gray-600">FTB</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            {revenueData ? (
              <Bar data={revenueData} options={barChartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading chart data...</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Analysis */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Year-to-Date Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {ytdPerformance?.net_subscriber_growth >= 0 ? '+' : ''}{formatNumber(ytdPerformance?.net_subscriber_growth || 0)}
            </div>
            <div className="text-sm text-gray-600 mb-1">Net Subscriber Growth</div>
            <div className={`text-xs font-medium ${
              (ytdPerformance?.subscriber_growth_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {ytdPerformance?.subscriber_growth_percent >= 0 ? '+' : ''}{ytdPerformance?.subscriber_growth_percent?.toFixed(1) || '0.0'}% YTD growth
            </div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600 mb-2">{ytdPerformance?.total_revenue_ytd?.toFixed(1) || '0.0'}B ₭</div>
            <div className="text-sm text-gray-600 mb-1">Total Revenue YTD</div>
            <div className={`text-xs font-medium ${
              (ytdPerformance?.revenue_growth_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {ytdPerformance?.revenue_growth_percent >= 0 ? '+' : ''}{ytdPerformance?.revenue_growth_percent?.toFixed(1) || '0.0'}% vs last year
            </div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600 mb-2">{ytdPerformance?.avg_collection_rate?.toFixed(1) || '0.0'}%</div>
            <div className="text-sm text-gray-600 mb-1">Avg Collection Rate</div>
            <div className="text-xs text-green-600 font-medium">+{ytdPerformance?.collection_improvement?.toFixed(1) || '0.0'}% improvement</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyAnalysis;